import { useEffect, useState } from 'react'

import { Section } from 'api/types'
import { SECTION_LABELS } from 'lib/format'
import { t } from 'lib/i18n'

import { Icon } from './Icon'
import { Button, TextAreaField } from './ui'

const RETURN_SECTIONS: Section[] = ['STAFF', 'LAB_TESTS', 'MATERIALS', 'EQUIPMENT', 'WORKS', 'VISITS']
const REASON_MAX = 500

/** AlertDialog · Return report (Figma 196:7539) — shared by ApprovalsPage and ReportPage. */
export const ReturnReportDialog = ({
  reportCode,
  engineerName,
  busy,
  onConfirm,
  onClose,
}: {
  reportCode: string
  engineerName?: string | null
  busy: boolean
  onConfirm: (note: string, sections: Section[]) => void
  onClose: () => void
}) => {
  const [note, setNote] = useState('')
  const [sections, setSections] = useState<Set<Section>>(new Set())

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const toggleSection = (section: Section) =>
    setSections((current) => {
      const next = new Set(current)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })

  const who = engineerName || t('المهندس', 'the engineer')

  return (
    <div className="modal-backdrop">
      <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="return-dialog-title" tabIndex={-1}>
        <div className="modal__body">
          <div className="alert-dialog__header">
            <div className="alert-dialog__icon alert-dialog__icon--danger">
              <Icon name="undo" size={20} />
            </div>
            <div className="alert-dialog__header-text">
              <h2 id="return-dialog-title">{t('إرسال التقرير للمراجعة؟', 'Send the report for review?')}</h2>
              <p className="muted small">
                {t(
                  `سيصبح ${reportCode} تقريراً للمراجعة لدى ${who}. سيُعلم بالسبب ويمكنه تعديله ثم إرساله من جديد.`,
                  `${reportCode} becomes a report for review with ${who}. They will see why, and they can edit it and submit it again.`
                )}
              </p>
            </div>
          </div>

          <div className="alert-dialog__sections">
            <p className="field__label">{t('ما الأقسام التي تحتاج تعديلاً؟', 'Which sections need changes?')}</p>
            <p className="muted small">{t('سيرى المهندس هذه الأقسام مُعلّمة في التقرير.', 'The engineer will see these sections marked on the report.')}</p>
            <div className="alert-dialog__checkbox-grid">
              {RETURN_SECTIONS.map((section) => (
                <label key={section} className="dropdown__item">
                  <input type="checkbox" checked={sections.has(section)} onChange={() => toggleSection(section)} />
                  {SECTION_LABELS[section]}
                </label>
              ))}
            </div>
          </div>

          <TextAreaField
            label={t('سبب المراجعة', 'Reason for review')}
            required
            rows={4}
            maxLength={REASON_MAX}
            hint={t(
              `مطلوب. سيرى ${who} هذه الملاحظة على تقرير للمراجعة. ${note.length}/${REASON_MAX}`,
              `Required. ${who} will see this note on the report for review. ${note.length}/${REASON_MAX}`
            )}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
        <footer className="modal__footer">
          <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
          <Button
            variant="danger"
            icon="undo"
            busy={busy}
            disabled={!note.trim()}
            onClick={() => onConfirm(note.trim(), Array.from(sections))}
          >
            {t('إرسال للمراجعة', 'Send for review')}
          </Button>
        </footer>
      </div>
    </div>
  )
}
